const BAG_VARIANT_ID = 47444117061820;
const QUALIFYING_SUBTOTAL = 15000;
const CAMPAIGN_NAME = "vip-members-bag";
const DISCOUNT_CODE = "VIP20";
const ACTIVE_KEY = "cpp:vip-members-bag:active";
const DISMISSED_KEY = "cpp:vip-members-bag:dismissed";
const BAG_PROPERTY = "_cpp_vip_members_bag";
const CART_UPDATE_EVENT = "shopify:cart:lines-update";

function lineProperties(item) {
  return item?.properties || {};
}

export function isVipBagLine(item) {
  return (
    Number(item?.variant_id) === BAG_VARIANT_ID && lineProperties(item)[BAG_PROPERTY] === "true"
  );
}

export function isInClinicGiftCardLine(item) {
  const properties = lineProperties(item);
  const handle = String(item?.handle || "").toLowerCase();
  const url = String(item?.url || "").toLowerCase();

  return (
    properties._gift_card_flow === "In Clinic Gift Cards" ||
    properties._cpp_gift_card_add_on === "true" ||
    handle === "in-clinic-gift-card" ||
    url.includes("/products/in-clinic-gift-card")
  );
}

export function qualifyingSubtotal(cart) {
  return (cart?.items || []).reduce((subtotal, item) => {
    if (Number(item?.variant_id) === BAG_VARIANT_ID || isInClinicGiftCardLine(item))
      return subtotal;

    const linePrice = Number(item?.original_line_price);
    if (Number.isFinite(linePrice)) return subtotal + linePrice;

    return subtotal + Number(item?.price || 0) * Number(item?.quantity || 0);
  }, 0);
}

export function planVipBagReconciliation(cart) {
  const bagLines = (cart?.items || []).filter(isVipBagLine);
  const eligible = qualifyingSubtotal(cart) >= QUALIFYING_SUBTOTAL;

  if (eligible && bagLines.length === 0) {
    return { action: "add", eligible, updates: {} };
  }

  const updates = {};
  if (eligible) {
    bagLines.forEach((line, index) => {
      const desiredQuantity = index === 0 ? 1 : 0;
      if (Number(line.quantity) !== desiredQuantity && line.key)
        updates[line.key] = desiredQuantity;
    });
  } else {
    bagLines.forEach((line) => {
      if (line.key) updates[line.key] = 0;
    });
  }

  return {
    action: Object.keys(updates).length > 0 ? (eligible ? "normalize" : "remove") : "none",
    eligible,
    updates,
  };
}

function readSession(key) {
  try {
    return window.sessionStorage.getItem(key);
  } catch (_error) {
    return null;
  }
}

function writeSession(key, value) {
  try {
    window.sessionStorage.setItem(key, value);
  } catch (_error) {}
}

function removeSession(key) {
  try {
    window.sessionStorage.removeItem(key);
  } catch (_error) {}
}

function readCookie(name) {
  const prefix = `${name}=`;
  const entry = document.cookie
    .split(";")
    .map((value) => value.trim())
    .find((value) => value.startsWith(prefix));

  if (!entry) return "";

  try {
    return decodeURIComponent(entry.slice(prefix.length));
  } catch (_error) {
    return entry.slice(prefix.length);
  }
}

function route(name, fallback) {
  return window.Theme?.routes?.[name] || `${window.Shopify?.routes?.root || "/"}${fallback}`;
}

function cartSectionIds() {
  return [...document.querySelectorAll("cart-items-component[data-section-id]")]
    .map((element) => element.dataset.sectionId)
    .filter(Boolean)
    .slice(0, 5);
}

async function fetchCart() {
  const response = await fetch(`${route("cart_url", "cart")}.js`, {
    headers: { Accept: "application/json" },
    credentials: "same-origin",
  });
  if (!response.ok) throw new Error(`Unable to read cart (${response.status}).`);
  return response.json();
}

function eventCart(cart) {
  return {
    id: cart.token,
    totalQuantity: cart.item_count,
    cost: {
      totalAmount: {
        amount: (Number(cart.total_price || 0) / 100).toFixed(2),
        currencyCode: cart.currency || "USD",
      },
    },
    lines: (cart.items || []).map((item) => ({
      id: item.key,
      quantity: item.quantity,
      cost: {
        totalAmount: {
          amount: (Number(item.final_line_price || 0) / 100).toFixed(2),
          currencyCode: cart.currency || "USD",
        },
      },
    })),
    discountCodes: [],
  };
}

function dispatchCartUpdate(action, cart, sections, lines) {
  const event = new Event(CART_UPDATE_EVENT, { bubbles: true });
  event.action = action;
  event.context = "cart";
  event.lines = lines;
  event.promise = Promise.resolve({
    cart: eventCart(cart),
    detail: {
      items: cart.items,
      itemCount: cart.item_count,
      sections,
      source: "cpp-vip-members-bag",
      didError: false,
    },
  });
  document.dispatchEvent(event);
}

async function mutateCart(endpoint, body) {
  const response = await fetch(endpoint, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
    },
    credentials: "same-origin",
    body: JSON.stringify(body),
  });
  const result = await response.json();
  if (!response.ok || result.status || result.errors) {
    throw new Error(
      result.description || result.message || result.errors || "Unable to update cart.",
    );
  }
  return result;
}

async function applyPlan(plan) {
  const sectionIds = cartSectionIds();
  const rendering = sectionIds.length
    ? {
        sections: sectionIds.join(","),
        sections_url: window.location.pathname,
      }
    : {};

  if (plan.action === "add") {
    const result = await mutateCart(route("cart_add_url", "cart/add.js"), {
      items: [
        {
          id: BAG_VARIANT_ID,
          quantity: 1,
          properties: { [BAG_PROPERTY]: "true" },
        },
      ],
      ...rendering,
    });
    const cart = await fetchCart();
    dispatchCartUpdate("add", cart, result.sections, [
      { merchandiseId: String(BAG_VARIANT_ID), quantity: 1 },
    ]);
    return cart;
  }

  const result = await mutateCart(route("cart_update_url", "cart/update.js"), {
    updates: plan.updates,
    ...rendering,
  });
  dispatchCartUpdate(plan.action === "remove" ? "remove" : "update", result, result.sections, []);
  return result;
}

function mountBanner() {
  if (readSession(DISMISSED_KEY) === "true") return;
  if (document.querySelector("[data-cpp-vip-members-bag]")) return;

  const headerGroup = document.querySelector("#header-group");
  if (!headerGroup) return;

  const banner = document.createElement("aside");
  banner.className = "cpp-vip-members-bag";
  banner.dataset.cppVipMembersBag = "";
  banner.setAttribute("role", "region");
  banner.setAttribute("aria-label", "VIP member offer");
  banner.innerHTML = `
    <img
      class="cpp-vip-members-bag__image"
      src="https://cdn.shopify.com/s/files/1/0680/7941/9580/files/gwp-side1.png?v=1788966468"
      alt="VIP Members Bag"
      width="56"
      height="56"
      loading="eager"
    >
    <p class="cpp-vip-members-bag__message">
      <strong>VIP member offer</strong>
      <span>Add $150 of products and receive a complimentary VIP Members Bag.</span>
    </p>
    <button class="cpp-vip-members-bag__dismiss" type="button" aria-label="Dismiss VIP member offer">
      <span aria-hidden="true">&times;</span>
    </button>
  `;

  banner.querySelector(".cpp-vip-members-bag__dismiss")?.addEventListener("click", function () {
    writeSession(DISMISSED_KEY, "true");
    banner.remove();
  });

  headerGroup.insertAdjacentElement("afterend", banner);
}

function startVipCampaign() {
  const params = new URLSearchParams(window.location.search);
  const campaignMatches = (params.get("utm_campaign") || "").toLowerCase() === CAMPAIGN_NAME;
  const discountMatches = readCookie("discount_code").toUpperCase() === DISCOUNT_CODE;

  if (campaignMatches && discountMatches) {
    writeSession(ACTIVE_KEY, "true");
    removeSession(DISMISSED_KEY);
  }

  if (readSession(ACTIVE_KEY) !== "true") return;

  let reconciliation = Promise.resolve();
  let scheduled = false;

  function scheduleReconciliation() {
    if (scheduled) return;
    scheduled = true;

    queueMicrotask(() => {
      scheduled = false;
      reconciliation = reconciliation
        .catch(() => {})
        .then(async () => {
          const cart = await fetchCart();
          const plan = planVipBagReconciliation(cart);
          if (plan.action !== "none") await applyPlan(plan);
        })
        .catch((error) => console.warn("[VIP Members Bag]", error));
    });
  }

  mountBanner();
  scheduleReconciliation();

  document.addEventListener(CART_UPDATE_EVENT, (event) => {
    if (event.promise) event.promise.then(scheduleReconciliation).catch(() => {});
    else scheduleReconciliation();
  });
  document.addEventListener("cart:updated", scheduleReconciliation);
  document.addEventListener("shopify:section:load", () => {
    mountBanner();
    scheduleReconciliation();
  });
  window.addEventListener("pageshow", scheduleReconciliation);
}

if (typeof window !== "undefined" && typeof document !== "undefined") {
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", startVipCampaign, { once: true });
  } else {
    startVipCampaign();
  }
}
